import {
  Quat,
  Operator,
  OperatorOutputMode,
  Registry,
  XfoOperatorInput,
  XfoOperatorOutput,
  Xfo,
  Vec3,
} from '@zeainc/zea-engine'

/** An operator for aiming items at targets.
 * @extends Operator
 */
class TriangleIKSolver extends Operator {
  align: Quat
  enabled: boolean
  joint1Offset: Vec3
  joint1TargetOffset: Vec3
  joint0Length: number
  joint1Length: number

  targetXfoInput = new XfoOperatorInput('Target')
  joint0XfoOutput = new XfoOperatorOutput('Joint0', OperatorOutputMode.OP_READ_WRITE)
  joint1XfoOutput = new XfoOperatorOutput('Joint1', OperatorOutputMode.OP_READ_WRITE)
  /**
   * Create a gears operator.
   * @param {string} name - The name value.
   */
  constructor(name?: string) {
    super(name)
    // this.addParameter(
    //   new MultiChoiceParameter('Axis', 0, ['+X Axis', '-X Axis', '+Y Axis', '-Y Axis', '+Z Axis', '-Z Axis'])
    // )
    this.addInput(this.targetXfoInput)
    this.addOutput(this.joint0XfoOutput)
    this.addOutput(this.joint1XfoOutput)
    this.align = new Quat()
    this.enabled = false
  }

  enable(): void{
    const targetXfo: Xfo = this.targetXfoInput.getValue()
    const joint0Xfo: Xfo = this.joint0XfoOutput.getValue()
    const joint1Xfo: Xfo = this.joint1XfoOutput.getValue()
    this.joint1Offset = joint0Xfo.inverse().multiply(joint1Xfo).tr
    this.joint1TargetOffset = joint1Xfo.inverse().multiply(targetXfo).tr
    this.joint1TargetOffset.normalizeInPlace()
    this.joint0Length = joint1Xfo.tr.distanceTo(joint0Xfo.tr)
    this.joint1Length = targetXfo.tr.distanceTo(joint1Xfo.tr)
    this.setDirty()
    this.enabled = true
  }

  /**
   * The evaluate method.
   */
  evaluate(): void {
    const targetXfo: Xfo = this.getInput('Target').getValue()
    const joint0Output = this.joint0XfoOutput
    const joint1Output = this.joint1XfoOutput
    const joint0Xfo: Xfo = joint0Output.getValue()
    const joint1Xfo: Xfo = joint1Output.getValue()

    ///////////////////////////////
    // Calc joint0Xfo
    const joint0TargetVec: Vec3 = targetXfo.tr.subtract(joint0Xfo.tr)
    const joint0TargetDist: number = joint0TargetVec.length()
    const joint01Vec = joint0Xfo.ori.rotateVec3(this.joint1Offset)

    // Calculate the angle using the rule of cosines.
    // cos C	= (a2 + b2 − c2)/2ab
    const a = this.joint0Length
    const b = joint0TargetDist
    const c = this.joint1Length
    const angle = Math.acos((a * a + b * b - c * c) / (2 * a * b))

    // console.log(currAngle, angle)

    joint01Vec.normalizeInPlace()
    joint0TargetVec.normalizeInPlace()

    const Joint0Axis = joint0TargetVec.cross(joint01Vec)
    const currAngle = joint0TargetVec.angleTo(joint01Vec)
    Joint0Axis.normalizeInPlace()

    this.align.setFromAxisAndAngle(Joint0Axis, angle - currAngle)
    joint0Xfo.ori = this.align.multiply(joint0Xfo.ori)

    ///////////////////////////////
    // Calc joint1Xfo
    joint1Xfo.tr = joint0Xfo.transformVec3(this.joint1Offset)

    const joint1TargetVec = targetXfo.tr.subtract(joint1Xfo.tr)
    joint1TargetVec.normalizeInPlace()
    this.align.setFrom2Vectors(joint1Xfo.ori.rotateVec3(this.joint1TargetOffset), joint1TargetVec)
    joint1Xfo.ori = this.align.multiply(joint1Xfo.ori)

    ///////////////////////////////
    // Done
    joint0Output.setClean(joint0Xfo)
    joint1Output.setClean(joint1Xfo)
  }
}

Registry.register('TriangleIKSolver', TriangleIKSolver)

export { TriangleIKSolver }
